/**
 * Cache em memória para requisições GET da API.
 * Usa estratégia stale-while-revalidate:
 *   - Se o cache está "fresh" (< freshTTL), retorna direto sem request.
 *   - Se está "stale" (> freshTTL e < maxTTL), retorna cache E faz request em background.
 *   - Se expirou (> maxTTL), faz request normal.
 */

class ApiCache {
  constructor() {
    /** @type {Map<string, {data: any, timestamp: number, headers: any}>} */
    this.store = new Map();

    // Tempo em ms que o cache é considerado "fresco" (retorna sem request)
    this.freshTTL = 2 * 60 * 1000; // 2 minutos

    // Tempo máximo que o cache pode ficar "stale" antes de ser descartado
    this.maxTTL = 10 * 60 * 1000; // 10 minutos

    // Tamanho máximo do cache (evita vazamento de memória)
    this.maxSize = 200;

    // Flag para controlar revalidações em andamento
    this.revalidating = new Set();
  }

  /**
   * Gera a chave do cache baseada na URL e params
   */
  buildKey(config) {
    const url = config.url || "";
    const params = config.params
      ? "?" + new URLSearchParams(config.params).toString()
      : "";
    const auth = config.headers?.Authorization || "";
    // Inclui auth para não misturar cache entre usuários
    return `${url}${params}|${auth.slice(-20)}`;
  }

  /**
   * Obtém dados do cache
   * @returns {{ data: any, isFresh: boolean } | null}
   */
  get(key) {
    const entry = this.store.get(key);
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;

    // Cache expirou completamente
    if (age > this.maxTTL) {
      this.store.delete(key);
      return null;
    }

    return {
      data: entry.data,
      headers: entry.headers,
      isFresh: age < this.freshTTL,
    };
  }

  /**
   * Salva dados no cache
   */
  set(key, data, headers = {}) {
    // Evita cache excessivo
    if (this.store.size >= this.maxSize) {
      // Remove a entrada mais antiga
      const oldestKey = this.store.keys().next().value;
      this.store.delete(oldestKey);
    }

    this.store.set(key, {
      data,
      headers,
      timestamp: Date.now(),
    });
  }

  /**
   * Invalida entradas que correspondem ao padrão
   */
  invalidate(pattern) {
    if (typeof pattern === "string") {
      // Invalida todas as keys que contêm o pattern
      for (const key of this.store.keys()) {
        if (key.includes(pattern)) {
          this.store.delete(key);
        }
      }
    }
  }

  /**
   * Limpa todo o cache
   */
  clear() {
    this.store.clear();
    this.revalidating.clear();
  }

  /**
   * Marca uma key como em revalidação
   */
  markRevalidating(key) {
    this.revalidating.add(key);
  }

  /**
   * Remove marca de revalidação
   */
  unmarkRevalidating(key) {
    this.revalidating.delete(key);
  }

  /**
   * Verifica se está revalidando
   */
  isRevalidating(key) {
    return this.revalidating.has(key);
  }
}

// Instância global singleton
const apiCache = new ApiCache();

export default apiCache;